'use client';

import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { AgentSettingsPanel } from '@/components/agent-details';
import { SettingsView } from '@/components/settings/settings-view';
import { Spinner } from '@/components/ui/spinner';

/**
 * Body of the `/agent-settings?username=<handle>` workspace dialog.
 *
 * One URL shape, `/u/<handle>/edit`, has always meant two things: your own
 * profile when the handle is yours, and an agent's settings when it names
 * one of your agents. Until 2026-09-27 both resolved to the Profile tab of
 * Settings, so `/u/<agent>/edit` and `?dialog=edit-profile&username=<agent>`
 * opened the OWNER's profile (e2e report, handoff 2026-09-26). This
 * component makes the call, with no round trip: the signed-in user's own
 * handle is known client-side, and anything else is handed to the agent
 * settings panel, which already answers "not found" for a handle that is
 * not an agent the person may configure.
 *
 * `strip @`: the emitter (`app/(portal)/(main)/u/[username]/edit/page.tsx`)
 * already strips it, the dialog action strips it again, and a hand-typed
 * URL may still carry one.
 */
export function AgentSettingsDialog({ username }: { username: string }) {
  const t = useT();
  const { user, loading } = useAuth();
  const handle = username.replace(/^@+/, '').trim();

  if (!handle) {
    return <p className="text-sm text-muted-foreground">{t('agents.notFound')}</p>;
  }
  // The session decides which surface this is; wait for it rather than
  // flash the agent panel's "not found" at someone editing their own profile.
  if (loading && !user) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="2xl" tone="muted" />
      </div>
    );
  }
  if (user && user.username.toLowerCase() === handle.toLowerCase()) {
    return <SettingsView embedded initialTab="profile" />;
  }
  return <AgentSettingsPanel agentIdOrName={handle} />;
}
